import { useEffect, useState } from "react";
import { Clock, Users, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { todayAST } from "@/lib/date";

const today = todayAST;

export default function RescheduleDialog({ appointment, open, onOpenChange, onDone }) {
  const [date, setDate] = useState(today());
  const [blocks, setBlocks] = useState([]);
  const [selBlock, setSelBlock] = useState(null);
  const [selSlot, setSelSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSlots = async (d) => {
    setSelBlock(null); setSelSlot(null);
    const { data } = await api.get(`/doctors/${appointment.doctor_id}/slots`, { params: { date: d } });
    setBlocks(data);
  };

  useEffect(() => {
    if (open) { setDate(today()); loadSlots(today()); }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => { if (open) loadSlots(date); /* eslint-disable-next-line */ }, [date]);

  const confirm = async () => {
    if (!selBlock) return;
    if (selBlock.modality === "HORAS" && !selSlot) { toast.error("Selecciona una hora"); return; }
    setBusy(true);
    try {
      await api.patch(`/appointments/${appointment.id}/reschedule`, {
        availability_id: selBlock.id,
        slot_time: selBlock.modality === "HORAS" ? selSlot : null,
      });
      toast.success("Cita reprogramada");
      onOpenChange(false);
      onDone && onDone();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-2xl bg-white" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[#0284C7]" /> Reprogramar cita</DialogTitle>
        </DialogHeader>
        <p className="text-sm font-semibold text-slate-500">{appointment?.doctor_name} · {appointment?.specialty}</p>

        <div className="mt-2">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Nueva fecha</label>
          <Input type="date" min={today()} value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 w-48 rounded-xl border-[#E2E8F0]" data-testid="reschedule-date" />
        </div>

        <div className="mt-3 space-y-3">
          {blocks.length === 0 && <p className="rounded-xl border border-dashed border-[#E2E8F0] p-6 text-center text-sm font-bold text-slate-400" data-testid="reschedule-no-slots">Sin disponibilidad para esta fecha.</p>}
          {blocks.map((b) => (
            <div key={b.id} className={`rounded-xl border p-4 ${selBlock?.id === b.id ? "border-[#0284C7] bg-sky-50/40" : "border-[#E2E8F0]"}`} data-testid={`rs-block-${b.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                  {b.modality === "HORAS" ? <Clock className="h-4 w-4 text-[#0284C7]" /> : <Users className="h-4 w-4 text-[#0284C7]" />}
                  {b.start_time}–{b.end_time} · {b.center_name}
                </div>
                <span className="rounded-full border border-[#E2E8F0] bg-white px-3 py-1 text-xs font-bold text-slate-500">
                  {b.modality === "HORAS" ? "Hora fija" : `${b.remaining_cupos} cupos`}
                </span>
              </div>
              {b.modality === "HORAS" ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {(b.available_slots || []).length === 0 && <span className="text-xs font-bold text-slate-400">Sin horas libres</span>}
                  {(b.available_slots || []).map((s) => (
                    <button key={s} onClick={() => { setSelBlock(b); setSelSlot(s); }} data-testid={`rs-slot-${b.id}-${s}`}
                      className={`rounded-full border px-3 py-1.5 text-xs font-bold ${selBlock?.id === b.id && selSlot === s ? "border-[#0284C7] bg-[#0284C7] text-white" : "border-[#E2E8F0] text-slate-600 hover:border-[#0284C7]"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              ) : (
                <button onClick={() => { setSelBlock(b); setSelSlot(null); }} disabled={b.remaining_cupos <= 0} data-testid={`rs-cupo-${b.id}`}
                  className={`mt-3 rounded-full border px-4 py-1.5 text-xs font-bold disabled:opacity-40 ${selBlock?.id === b.id ? "border-[#0284C7] bg-[#0284C7] text-white" : "border-[#E2E8F0] text-slate-600"}`}>
                  {selBlock?.id === b.id ? "Seleccionado" : "Tomar cupo por llegada"}
                </button>
              )}
            </div>
          ))}
        </div>

        <Button onClick={confirm} disabled={busy || !selBlock} className="mt-4 w-full rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="reschedule-confirm-btn">
          {busy ? "Reprogramando…" : "Confirmar nueva cita"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
